import { type DateInput, toDate } from './date';

/**
 * Source of "now" for business logic (D-013). Domain code receives a Clock instead of calling
 * `new Date()` / `Date.now()`, so tests can pin or advance time.
 */
export interface Clock {
  now(): Date;
}

/** The real clock. This is the only place that should read the system time. */
export const systemClock: Clock = Object.freeze({
  now: () => new Date(),
});

/** A clock frozen at one instant. Every call returns a fresh Date for that instant. */
export function fixedClock(at: DateInput): Clock {
  const ms = toDate(at).getTime();
  return Object.freeze({ now: () => new Date(ms) });
}

export interface AdvanceBy {
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
}

/** A controllable clock for tests: starts at `start` and only moves when told to. */
export interface TestClock extends Clock {
  /** Jump to an absolute instant. */
  set(at: DateInput): void;
  /** Move forward (or backward, with negative values) by a duration. */
  advance(by: number | AdvanceBy): void;
}

const MS = { days: 86_400_000, hours: 3_600_000, minutes: 60_000, seconds: 1_000, milliseconds: 1 };

export function createTestClock(start: DateInput = '2026-01-01T00:00:00.000Z'): TestClock {
  let current = toDate(start).getTime();
  return {
    now: () => new Date(current),
    set(at) {
      current = toDate(at).getTime();
    },
    advance(by) {
      const delta =
        typeof by === 'number'
          ? by
          : (Object.keys(MS) as (keyof AdvanceBy)[]).reduce(
              (sum, unit) => sum + (by[unit] ?? 0) * MS[unit],
              0,
            );
      if (!Number.isFinite(delta)) throw new RangeError('TestClock.advance: delta must be finite');
      current += delta;
    },
  };
}
